import { TaskType } from '@/shared/domain/taskTypes';
import { getLanguageName } from '@/shared/utils/helpers';
import { languageSharePercent } from '@/features/datasets/utils/datasetSummaryStats';
import { data_from_server_post } from '@/shared/http/client';
import { getTrainBenchmarkCrossOutputInsights } from '@/features/trainings/utils/labelMismatchUtils';

/**
 * Pure analysis helpers extracted from `pages/DataCollection.jsx` so the
 * task landing card, and any future host
 * of the unified `DataOverviewCard` share one implementation. No React state
 * here — every helper takes the usecase / context it needs as arguments.
 */

const PERCENTAGE_LANGUAGE_THRESHOLD = 10;

/**
 * Insight `target` values control which panel renders the insight in the
 * Data Overview card:
 *  - `examples` -> left column "Insights" panel — findings about the
 *                  example content (long examples, language mismatch) plus
 *                  the "Your data looks good" fallback when nothing fires.
 *  - `output`   -> panel under "Output Detected" on the right — findings
 *                  about the label/entity/relation distribution
 *                  (insufficient, unbalanced, multilabel mismatch).
 *
 * Untagged insights default to `examples` (the legacy single-panel slot).
 * Both arrays are derived once in `processAnalysisData` so the render side
 * never has to know the routing rule.
 */
export const InsightTarget = Object.freeze({
    EXAMPLES: 'examples',
    OUTPUT: 'output',
});

const withTarget = (insight, target) => ({ ...insight, target });

const callServer = (endpoint, body) =>
    new Promise((resolve, reject) => {
        data_from_server_post(endpoint, body, resolve, reject);
    });

export async function fetchUsecaseDatasetMetadata(usecase) {
    if (!usecase?._id || !usecase?.client_id) {
        return { dataset: null, benchmark: null, optuneSpecificInfo: null, datasetsInfo: null };
    }
    try {
        const response = await callServer('datasets-and-benchmarks-meta-data', {
            client_id: usecase.client_id,
            llm_usecase_id: usecase._id,
        });
        return {
            dataset: response?.dataset,
            benchmark: response?.benchmark,
            optuneSpecificInfo: response?.optune_specific_info,
            datasetsInfo: response?.datasets_info || [],
        };
    } catch (error) {
        console.error('fetchUsecaseDatasetMetadata failed:', error);
        return { dataset: null, benchmark: null, optuneSpecificInfo: null, datasetsInfo: [] };
    }
}

export async function fetchLabelCounts({
    usecaseId,
    isBenchmark = false,
    isManualModelPredictions = false,
}) {
    if (!usecaseId) {
        return {
            label_counts: {},
            entity_counts: {},
            relation_counts: {},
            tool_counts: {},
            unlabeled_count: { total: 0 },
            none_selected_count: { total: 0 },
        };
    }
    try {
        const response = await callServer('get-label-counts', {
            llm_usecase_id: usecaseId,
            is_benchmark: isBenchmark,
            is_manual_model_predictions: isManualModelPredictions,
        });
        return {
            label_counts: response?.label_counts || {},
            entity_counts: response?.entity_counts || {},
            relation_counts: response?.relation_counts || {},
            // Agentic tasks: `{ toolName: { total } }` of assistant tool calls
            // across active records (cached server-side like label counts).
            tool_counts: response?.tool_counts || {},
            unlabeled_count: response?.unlabeled_count || { total: 0 },
            none_selected_count: response?.none_selected_count || { total: 0 },
        };
    } catch (error) {
        console.error('fetchLabelCounts failed:', error);
        return {
            label_counts: {},
            entity_counts: {},
            relation_counts: {},
            tool_counts: {},
            unlabeled_count: { total: 0 },
            none_selected_count: { total: 0 },
        };
    }
}

export async function fetchLongTextData({ usecaseId, isBenchmark = false }) {
    if (!usecaseId) return null;
    try {
        const response = await callServer('get-long-text-count', {
            llm_usecase_id: usecaseId,
            is_benchmark: isBenchmark,
        });
        return response?.success ? response : null;
    } catch (error) {
        console.error('fetchLongTextData failed:', error);
        return null;
    }
}

export async function fetchCodeExampleData({ usecaseId, isBenchmark = false }) {
    if (!usecaseId) return null;
    try {
        const response = await callServer('get-code-example-count', {
            llm_usecase_id: usecaseId,
            is_benchmark: isBenchmark,
        });
        return response?.success ? response : null;
    } catch (error) {
        console.error('fetchCodeExampleData failed:', error);
        return null;
    }
}

function generateAdditionalInsights({
    objectCounts,
    totalExamples,
    longTextData,
    taskType,
    activeTab = 'entities',
}) {
    const additionalInsights = [];
    let counts = objectCounts;
    if (taskType === TaskType.classification) {
        counts = objectCounts?.label_counts;
    } else if (taskType === TaskType.NER) {
        counts = objectCounts?.entity_counts;
    } else if (taskType === TaskType.NER_WITH_RELATIONS) {
        if (activeTab === 'entities') {
            counts = objectCounts?.entity_counts;
        } else if (activeTab === 'relations') {
            counts = objectCounts?.relation_counts;
        } else {
            return additionalInsights;
        }
    } else {
        return additionalInsights;
    }

    if (counts && totalExamples > 0) {
        const labelsWithInsufficientSamples = Object.entries(counts).filter(
            ([, labelData]) => (labelData?.total || 0) < 15
        );
        if (labelsWithInsufficientSamples.length > 0) {
            const numLabels = labelsWithInsufficientSamples.length;
            const noun =
                taskType === TaskType.classification
                    ? 'label'
                    : activeTab === 'relations'
                      ? 'relation'
                      : 'entity';
            additionalInsights.push(
                withTarget(
                    {
                        type: 'warning',
                        content: `${numLabels} ${noun}${numLabels > 1 ? "s don't" : " doesn't"} have enough examples`,
                        explanation:
                            'Labels with fewer than 15 examples may not provide enough data for effective model training.',
                    },
                    InsightTarget.OUTPUT,
                ),
            );
        }
    }

    if (counts && totalExamples > 0) {
        const labelPercentages = Object.entries(counts)
            .map(([, labelData]) => ((labelData?.total || 0) / totalExamples) * 100)
            .filter((percentage) => percentage > 0);
        if (labelPercentages.length > 1) {
            const ratio = Math.max(...labelPercentages) / Math.min(...labelPercentages);
            if (ratio > 5) {
                additionalInsights.push(
                    withTarget(
                        {
                            type: 'warning',
                            content: `${
                                taskType === TaskType.classification
                                    ? 'Labels'
                                    : activeTab === 'relations'
                                      ? 'Relations'
                                      : 'Entities'
                            } seem to be unbalanced`,
                            explanation: `The most frequent label is ${ratio.toFixed(
                                1
                            )}x more common than the least frequent label, which may lead to biased model performance.`,
                        },
                        InsightTarget.OUTPUT,
                    ),
                );
            }
        }
    }

    if (longTextData && longTextData.long_text_count !== undefined && totalExamples > 0) {
        const denominator = longTextData.total_count || totalExamples;
        const longTextPercentage =
            denominator > 0 ? (longTextData.long_text_count / denominator) * 100 : 0;
        if (longTextPercentage > 10) {
            additionalInsights.push(
                withTarget(
                    {
                        type: 'warning',
                        content: `${Math.round(longTextPercentage)}% of the dataset has very long examples`,
                        explanation:
                            'A high percentage of examples exceed 512 tokens, which may impact model performance and training efficiency.',
                    },
                    InsightTarget.EXAMPLES,
                ),
            );
        }
    }

    return additionalInsights;
}

function generateMultilabelMismatchInsight({ usecase, datasetsInfo }) {
    if (!usecase || !datasetsInfo) return null;
    if (usecase.is_multilabel) return null;
    const hasMultilabelDatasets = datasetsInfo.some((info) => info?.is_multilabel);
    if (!hasMultilabelDatasets) return null;
    return withTarget(
        {
            type: 'warning',
            content: 'Multilabel dataset detected in single-label task',
            explanation:
                'You have multilabel datasets but your task is configured for single-label classification. Only the first label from each example will be used during training.',
        },
        InsightTarget.OUTPUT,
    );
}

/**
 * Warn when a classification split contains records with no label assigned
 * (``response.labels`` missing or ``[]``). These "Unlabeled" rows are excluded
 * from training until they are labeled, so surfacing the count lets the user
 * decide whether to label or drop them. The multilabel "None Selected" sentinel
 * (``[""]``) is a valid category and is counted separately, so it does not
 * trigger this warning. Routed to the right-hand "Output Detected" panel next
 * to "Labels Detected" (not the left "# of Examples" insights column).
 */
function generateUnlabeledRecordsInsight({ usecase, unlabeledCount }) {
    if (usecase?.task_type !== TaskType.classification) return null;
    if (!unlabeledCount || unlabeledCount <= 0) return null;
    const isSingle = unlabeledCount === 1;
    return withTarget(
        {
            type: 'warning',
            content: `${unlabeledCount} unlabeled ${isSingle ? 'record' : 'records'}`,
            explanation:
                'Some records have no label assigned. Unlabeled records are excluded from training until they are labeled — label them or exclude them on import.',
        },
        InsightTarget.OUTPUT,
    );
}

function generateLanguageMismatchInsight({ usecase, languages, totalExamples }) {
    if (!usecase || !languages || Object.keys(languages).length === 0 || !totalExamples) {
        return null;
    }
    const taskLanguages = usecase.task_languages || [];
    if (taskLanguages.length === 0) return null;

    const detectedLanguages = Object.entries(languages)
        .filter(([, count]) => languageSharePercent(count, languages) > PERCENTAGE_LANGUAGE_THRESHOLD)
        .map(([lang]) => lang);
    if (detectedLanguages.length === 0) return null;

    const detectedSet = new Set(detectedLanguages);
    const taskSet = new Set(taskLanguages);
    const unexpectedLanguages = detectedLanguages.filter((lang) => !taskSet.has(lang));
    const missingLanguages = taskLanguages.filter((lang) => !detectedSet.has(lang));

    if (unexpectedLanguages.length === 0 && missingLanguages.length === 0) return null;

    let content = '';
    let explanation = '';
    if (unexpectedLanguages.length > 0 && missingLanguages.length > 0) {
        const unexpectedNames = unexpectedLanguages.map(getLanguageName).join(', ');
        const missingNames = missingLanguages.map(getLanguageName).join(', ');
        content = 'Language mismatch detected';
        explanation = `Your data contains ${unexpectedNames} which ${
            unexpectedLanguages.length === 1 ? 'is' : 'are'
        } not in your task's supported languages. Your task expects ${missingNames} which ${
            missingLanguages.length === 1 ? 'is' : 'are'
        } not detected in the data.`;
    } else if (unexpectedLanguages.length > 0) {
        const unexpectedNames = unexpectedLanguages.map(getLanguageName).join(', ');
        content = 'Unexpected languages detected in data';
        explanation = `Your data contains ${unexpectedNames} which ${
            unexpectedLanguages.length === 1 ? 'is' : 'are'
        } not in your task's supported languages (${taskLanguages.map(getLanguageName).join(', ')}).`;
    } else {
        const missingNames = missingLanguages.map(getLanguageName).join(', ');
        content = 'Expected languages not detected in data';
        explanation = `Your task expects ${missingNames} but ${
            missingLanguages.length === 1 ? 'this language is' : 'these languages are'
        } not detected in your data.`;
    }
    return withTarget({ type: 'warning', content, explanation }, InsightTarget.EXAMPLES);
}

/**
 * Combine raw analysis fields into the same shape `DataAnalysisSection` already
 * consumes today. Pure — depends only on its arguments.
 */
export function processAnalysisData({
    info,
    objectCounts,
    longTextData,
    codeExampleData,
    numFiles,
    datasetsInfo,
    isBenchmark = false,
    usecase,
    activeTab = 'entities',
}) {
    if (!info || !usecase) return null;

    const processedExamples = info.num_records || 0;
    const totalExamplesInFile = (info.total_num_records ?? processedExamples) || 0;
    const languages = info.language || {};
    const isCode = info.is_code;
    const isPunctuationDetected = info.is_punctuation_detected === true;
    // Backend-supplied insights (currently always empty; backend reserves the
    // shape via the `Insight` Pydantic model). Default any untagged backend
    // insight to the left "examples" panel so the existing visual location is
    // preserved if/when the backend starts populating this field.
    const insights = (info.insights || []).map((insight) =>
        insight?.target ? insight : withTarget(insight, InsightTarget.EXAMPLES),
    );
    const significantLanguages = info.significant_languages;
    const numLabels = Object.keys(objectCounts?.label_counts || {}).length;
    const numEntities = Object.keys(objectCounts?.entity_counts || {}).length;
    const numRelations = Object.keys(objectCounts?.relation_counts || {}).length;

    let shouldBeCode = isCode;
    if (isCode !== null && isCode !== undefined) {
        shouldBeCode = isCode;
    } else if (isCode === null && codeExampleData) {
        const codePercentage = codeExampleData.code_example_count / codeExampleData.total_count;
        shouldBeCode = codePercentage > 0.5;
    } else if (isCode === true) {
        shouldBeCode = true;
    }

    const additionalInsights = generateAdditionalInsights({
        objectCounts,
        totalExamples: processedExamples,
        longTextData,
        taskType: usecase.task_type,
        activeTab,
    });

    if (!isBenchmark) {
        const multilabel = generateMultilabelMismatchInsight({ usecase, datasetsInfo });
        if (multilabel) additionalInsights.push(multilabel);
    }

    const unlabeledCount = objectCounts?.unlabeled_count?.total || 0;
    const unlabeledInsight = generateUnlabeledRecordsInsight({ usecase, unlabeledCount });
    if (unlabeledInsight) additionalInsights.push(unlabeledInsight);

    const languageMismatch = generateLanguageMismatchInsight({
        usecase,
        languages,
        totalExamples: processedExamples,
    });
    if (languageMismatch) additionalInsights.push(languageMismatch);

    const allInsights = [...insights, ...additionalInsights];

    return {
        metrics: {
            files: numFiles || 0,
            labels: numLabels,
            entities: numEntities,
            relations: numRelations,
            examples: processedExamples,
        },
        languages,
        significantLanguages,
        isCode: shouldBeCode,
        isPunctuationDetected,
        labels: objectCounts?.label_counts || {},
        entities: objectCounts?.entity_counts || {},
        relations: objectCounts?.relation_counts || {},
        toolCalls: objectCounts?.tool_counts || {},
        // `insights` kept as the merged union for backward compat with any
        // future consumer; render side reads the partitioned arrays below.
        insights: allInsights,
        examplesInsights: allInsights.filter(
            (i) => (i?.target ?? InsightTarget.EXAMPLES) === InsightTarget.EXAMPLES,
        ),
        outputInsights: allInsights.filter((i) => i?.target === InsightTarget.OUTPUT),
        totalExamples: processedExamples,
        totalExamplesInFile,
        numCharacters: info.num_characters,
        unlabeledCount,
    };
}

const sumObjectCountTotals = (countsDict) => {
    if (!countsDict || typeof countsDict !== 'object') return 0;
    return Object.values(countsDict).reduce((acc, entry) => acc + (entry?.total || 0), 0);
};

/**
 * When `datasets-and-benchmarks-meta-data` reports `num_records === 0` (e.g.
 * active-record filter lags or disagrees with cached label histograms),
 * still surface counts from `get-label-counts` so Data Overview matches
 * training data that exists on the usecase.
 */
const inferExamplesFromObjectCounts = (objectCounts, taskType, activeTab) => {
    if (taskType === TaskType.classification) {
        return sumObjectCountTotals(objectCounts?.label_counts);
    }
    if (taskType === TaskType.NER) {
        return sumObjectCountTotals(objectCounts?.entity_counts);
    }
    if (taskType === TaskType.NER_WITH_RELATIONS) {
        if (activeTab === 'relations') {
            return sumObjectCountTotals(objectCounts?.relation_counts);
        }
        return sumObjectCountTotals(objectCounts?.entity_counts);
    }
    return 0;
};

const mergeInfoWithInferredRecords = (info, objectCounts, usecase, activeTab) => {
    const n = Number(info?.num_records) || 0;
    if (n > 0) return info;
    const inferred = inferExamplesFromObjectCounts(objectCounts, usecase?.task_type, activeTab);
    if (inferred <= 0) return info;
    return { ...info, num_records: inferred };
};

/**
 * Append cross-split train vs benchmark insights so both tabs show the same
 * error under "Output Detected" / Insights.
 */
const appendCrossSplitOutputInsights = (processed, crossInsights) => {
    if (!processed || !crossInsights?.length) return processed;
    const tagged = crossInsights.map((i) => withTarget(i, InsightTarget.OUTPUT));
    return {
        ...processed,
        insights: [...(processed.insights || []), ...tagged],
        outputInsights: [...(processed.outputInsights || []), ...tagged],
    };
};

/**
 * High-level helper: fetch metadata + per-file counts and return the same
 * `{ data, benchmarkData, externalModelCounts, datasetsInfo, optuneSpecificInfo }`
 * shape that the page state holds today.
 *
 * `onMetadata` (optional) fires as soon as the lightweight
 * `datasets-and-benchmarks-meta-data` call resolves — before the heavier
 * per-file count fetches — so callers can surface `datasetsInfo` (file sizes,
 * origin / source_config for the AI-generated / AI-retrieved pills) without
 * waiting for the full analysis.
 */
export async function fetchFullAnalysisForUsecase({
    usecase,
    activeTab = 'entities',
    onMetadata,
} = {}) {
    if (!usecase?._id) {
        return {
            data: null,
            benchmarkData: null,
            externalModelCounts: null,
            datasetsInfo: [],
            optuneSpecificInfo: null,
        };
    }

    const { dataset, benchmark, optuneSpecificInfo, datasetsInfo } =
        await fetchUsecaseDatasetMetadata(usecase);
    onMetadata?.({ datasetsInfo: datasetsInfo || [], optuneSpecificInfo });

    const datasets = usecase.datasets || [];
    const benchmarks = usecase.benchmarks || [];
    const manualPredictions = usecase.manual_model_predictions || [];

    let trainObjectCounts = null;
    let processedData = null;
    if (dataset && datasets.length > 0) {
        const [objectCounts, longTextData, codeExampleData] = await Promise.all([
            fetchLabelCounts({ usecaseId: usecase._id, isBenchmark: false }),
            fetchLongTextData({ usecaseId: usecase._id, isBenchmark: false }),
            fetchCodeExampleData({ usecaseId: usecase._id, isBenchmark: false }),
        ]);
        trainObjectCounts = objectCounts;
        const trainInfo = mergeInfoWithInferredRecords(dataset, objectCounts, usecase, activeTab);
        processedData = processAnalysisData({
            info: trainInfo,
            objectCounts,
            longTextData,
            codeExampleData,
            numFiles: datasets.length,
            datasetsInfo,
            isBenchmark: false,
            usecase,
            activeTab,
        });
    }

    let benchmarkObjectCounts = null;
    let processedBenchmarkData = null;
    if (benchmark && benchmarks.length > 0) {
        const [objectCounts, longTextData, codeExampleData] = await Promise.all([
            fetchLabelCounts({ usecaseId: usecase._id, isBenchmark: true }),
            fetchLongTextData({ usecaseId: usecase._id, isBenchmark: true }),
            fetchCodeExampleData({ usecaseId: usecase._id, isBenchmark: true }),
        ]);
        benchmarkObjectCounts = objectCounts;
        const benchmarkInfo = mergeInfoWithInferredRecords(
            benchmark,
            objectCounts,
            usecase,
            activeTab
        );
        processedBenchmarkData = processAnalysisData({
            info: benchmarkInfo,
            objectCounts,
            longTextData,
            codeExampleData,
            numFiles: benchmarks.length,
            datasetsInfo,
            isBenchmark: true,
            usecase,
            activeTab,
        });
    }

    if (trainObjectCounts && benchmarkObjectCounts) {
        const crossRaw = getTrainBenchmarkCrossOutputInsights({
            taskType: usecase.task_type,
            datasetCounts: trainObjectCounts,
            benchmarkCounts: benchmarkObjectCounts,
        });
        processedData = appendCrossSplitOutputInsights(processedData, crossRaw);
        processedBenchmarkData = appendCrossSplitOutputInsights(processedBenchmarkData, crossRaw);
    }

    let externalModelCounts = null;
    if (manualPredictions.length > 0) {
        externalModelCounts = await fetchLabelCounts({
            usecaseId: usecase._id,
            isBenchmark: false,
            isManualModelPredictions: true,
        });
    }

    return {
        data: processedData,
        benchmarkData: processedBenchmarkData,
        externalModelCounts,
        datasetsInfo: datasetsInfo || [],
        optuneSpecificInfo,
    };
}
